import { interpretAsDocumentId, isValidAutomergeUrl, parseAutomergeUrl, stringifyAutomergeUrl, } from "@automerge/automerge-repo/slim";
import { subscribe } from "./index.js";
import { forwardingProxy } from "./forwarding-proxy.js";
import { OverlayHandle } from "./overlay-handle.js";
// The only members whose behavior an overlay repo changes; everything else
// (create/create2/clone/delete, the EventEmitter surface, ...) forwards to the
// realm-local base repo.
const OVERLAY_REPO_OWNED = new Set([
    "baseRepo",
    "handles",
    "find",
    "findWithProgress",
    "create",
    "create2",
    "dispose",
]);
/**
 * A realm-local {@link RepoLike} shim that makes document resolution
 * remappable across provider scopes (including iframes) without sending a live
 * `Repo`/`DocHandle` over the wire.
 *
 * `find`/`findWithProgress` open a persistent `repo:handle-descriptor`
 * subscription for the requested url and resolve the returned
 * `cloneUrl ?? url` against the realm-local `baseRepo`, then hand back an
 * {@link OverlayHandle} that keeps reporting the *original* url. Follow-up
 * descriptor emissions re-point the live wrapper via `swapBackingDocHandle`.
 * Every other method (`create`, `create2`, `clone`, `delete`, the
 * EventEmitter surface, ...) forwards to `baseRepo` unchanged — created docs
 * need no remapping.
 *
 * The owning element must call {@link dispose} on disconnect to release the
 * descriptor subscriptions.
 */
export class OverlayRepo {
    baseRepo;
    #element;
    // Keyed by the *presented* url (documentId + path + heads), not just the
    // documentId, so distinct sub-document paths under the same root document
    // don't collide.
    #wrapped = new Map();
    #inner = new Map();
    #resolving = new Map();
    // Live descriptor subscriptions, one per presented url.
    #subscriptions = new Map();
    // Applied backing url per presented url; skips no-op re-emissions.
    #backingUrls = new Map();
    // Progress subscribers per presented url, mapped to their unsubscribe from
    // the current inner; re-wired on swap.
    #progressDispatchers = new Map();
    #disposed = false;
    constructor(baseRepo, element) {
        this.baseRepo = baseRepo;
        this.#element = element;
        return forwardingProxy(this, baseRepo, OVERLAY_REPO_OWNED);
    }
    // Only already-wrapped handles are exposed; unknown ids fall through to
    // `find`/`findWithProgress` so consumers never see an un-wrapped handle.
    // `#wrapped` is keyed by presented url, so derive the documentId from each
    // handle; sub-handles of the same root collapse onto a single entry (the
    // `Record<DocumentId, ...>` shape can't represent paths).
    get handles() {
        const out = {};
        for (const wrapped of this.#wrapped.values()) {
            out[wrapped.documentId] = wrapped;
        }
        return out;
    }
    async find(id) {
        const presented = presentedUrlOf(id);
        const cached = this.#wrapped.get(presented);
        if (cached)
            return cached;
        const wrapped = await this.#resolve(presented);
        return wrapped;
    }
    // Mirrors the realm-local repo's progress but reports `loading` until the
    // descriptor has arrived and the backing handle is wrapped — otherwise
    // consumers would briefly observe the un-wrapped (un-remapped) handle.
    findWithProgress(id) {
        const self = this;
        const presented = presentedUrlOf(id);
        // The reported `documentId` is the root id — `DocumentId` can't encode a
        // path — but caches are keyed by the full presented url.
        const documentId = parseAutomergeUrl(presented).documentId;
        const wrappedPromise = this.#resolve(presented);
        wrappedPromise.catch(() => { });
        const peek = () => {
            const inner = self.#inner.get(presented);
            if (!inner)
                return { state: "loading", sources: {} };
            const innerState = inner.peek();
            if (innerState.state !== "ready")
                return innerState;
            const wrapped = self.#wrapped.get(presented);
            if (!wrapped) {
                return { state: "loading", sources: innerState.sources };
            }
            return {
                state: "ready",
                handle: wrapped,
                sources: innerState.sources,
            };
        };
        return {
            documentId,
            peek,
            subscribe: (callback) => {
                // `closed` covers the unsubscribe-before-resolve race: without it the
                // resolution settling later would still subscribe to the inner
                // progress (leaking that subscription forever) and invoke the
                // consumer's callback after it unsubscribed.
                let closed = false;
                let last = null;
                let registered = false;
                const dispatch = () => {
                    if (closed)
                        return;
                    const state = peek();
                    const sig = state.state === "failed"
                        ? `failed:${state.error.message}`
                        : state.state;
                    if (sig === last)
                        return;
                    last = sig;
                    callback(state);
                };
                // Registered via the dispatcher registry (not on the inner directly)
                // so a backing swap can re-wire this subscriber onto the new inner.
                wrappedPromise.then(() => {
                    if (closed)
                        return;
                    self.#registerProgressDispatcher(presented, dispatch);
                    registered = true;
                    dispatch();
                }, dispatch);
                return () => {
                    closed = true;
                    if (registered) {
                        self.#unregisterProgressDispatcher(presented, dispatch);
                    }
                };
            },
            whenReady: ({ signal } = {}) => {
                const handlePromise = wrappedPromise;
                if (signal?.aborted)
                    return Promise.reject(signal.reason);
                if (!signal)
                    return handlePromise;
                return new Promise((resolve, reject) => {
                    const onAbort = () => reject(signal.reason);
                    signal.addEventListener("abort", onAbort, { once: true });
                    handlePromise.then((handle) => {
                        signal.removeEventListener("abort", onAbort);
                        resolve(handle);
                    }, (err) => {
                        signal.removeEventListener("abort", onAbort);
                        reject(err);
                    });
                });
            },
            // Deprecated; kept for pre-`peek()` consumers.
            get state() {
                return peek().state;
            },
            get progress() {
                return self.#inner.get(presented)?.progress;
            },
            get error() {
                const s = peek();
                return s.state === "failed" ? s.error : undefined;
            },
        };
    }
    // Plain local creates need no remapping; declared (rather than forwarded) so
    // the class still satisfies `RepoLike`.
    create(initialValue) {
        return this.baseRepo.create(initialValue);
    }
    create2(initialValue) {
        return this.baseRepo.create2(initialValue);
    }
    /** Called by the owning element on disconnect; releases live resources. */
    dispose() {
        if (this.#disposed)
            return;
        this.#disposed = true;
        for (const unsubscribe of this.#subscriptions.values())
            unsubscribe();
        this.#subscriptions.clear();
        for (const dispatchers of this.#progressDispatchers.values()) {
            for (const unsubscribeInner of dispatchers.values())
                unsubscribeInner();
        }
        this.#progressDispatchers.clear();
        for (const wrapped of this.#wrapped.values())
            wrapped.dispose();
        this.#wrapped.clear();
        this.#inner.clear();
        this.#resolving.clear();
        this.#backingUrls.clear();
    }
    // De-dupes concurrent resolutions: the first caller opens the persistent
    // descriptor subscription, everyone else awaits the same promise. Later
    // emissions swap the resolved wrapper's backing in place.
    #resolve(presented) {
        const existing = this.#resolving.get(presented);
        if (existing)
            return existing;
        // Don't open subscriptions nobody will tear down; the element is gone.
        if (this.#disposed) {
            return Promise.reject(new Error("OverlayRepo is disposed; cannot resolve " + presented));
        }
        // The descriptor channel remaps whole documents, so ask about the root
        // and reapply the original path/heads onto whatever clone comes back.
        // The clone is a fork of the root, so the same sub-tree exists in it.
        const { documentId, segments, heads } = parseAutomergeUrl(presented);
        const rootUrl = stringifyAutomergeUrl({ documentId });
        const promise = new Promise((resolve, reject) => {
            // Only the latest received descriptor may commit its backing.
            let seq = 0;
            const apply = async (descriptor) => {
                const mySeq = ++seq;
                const backingRoot = descriptor.cloneUrl ?? descriptor.url;
                // A remapper may pin the backing to specific heads by stamping them
                // onto the returned url; honor them, but presented-url heads win.
                const parsedBacking = parseAutomergeUrl(backingRoot);
                const backingUrl = stringifyAutomergeUrl({
                    documentId: parsedBacking.documentId,
                    segments,
                    heads: heads ?? parsedBacking.heads,
                });
                if (this.#backingUrls.get(presented) === backingUrl)
                    return;
                const inner = this.baseRepo.findWithProgress(backingUrl);
                const backing = await this.baseRepo.find(backingUrl);
                if (mySeq !== seq || this.#disposed)
                    return;
                this.#backingUrls.set(presented, backingUrl);
                this.#setInner(presented, inner);
                const wrapped = this.#wrapped.get(presented);
                if (wrapped) {
                    wrapped.swapBackingDocHandle(backing);
                    return;
                }
                const created = new OverlayHandle({
                    presentedUrl: presented,
                    backing,
                });
                this.#wrapped.set(presented, created);
                resolve(created);
            };
            const unsubscribe = subscribe(this.#element, { type: "repo:handle-descriptor", url: rootUrl }, (descriptor) => {
                apply(descriptor).catch((err) => {
                    // Only the initial resolution can fail the returned promise;
                    // a failed re-map keeps the previous backing.
                    if (!this.#wrapped.has(presented))
                        reject(err);
                    else {
                        console.error(`[patchwork-providers] failed to re-map ${presented}:`, err);
                    }
                });
            });
            this.#subscriptions.set(presented, unsubscribe);
        });
        // Only successful resolutions stay memoized. A rejection — typically
        // baseRepo.find reporting unavailable because the doc (or its keyhive
        // access) hasn't synced yet — is retryable, so caching it would pin every
        // future find() of this url to the same stale rejection and defeat the
        // views' "retry once access syncs" recovery. Evict so the next find()
        // re-runs the whole resolution. The identity check keeps a late-arriving
        // cleanup from clobbering a newer in-flight attempt.
        promise.catch(() => {
            if (this.#resolving.get(presented) === promise) {
                this.#resolving.delete(presented);
                this.#inner.delete(presented);
            }
        });
        this.#resolving.set(presented, promise);
        return promise;
    }
    // Attach a progress subscriber to the current inner (if any), keeping its
    // unsubscribe so `#setInner` can re-wire it on a swap.
    #registerProgressDispatcher(presented, dispatch) {
        let dispatchers = this.#progressDispatchers.get(presented);
        if (!dispatchers) {
            dispatchers = new Map();
            this.#progressDispatchers.set(presented, dispatchers);
        }
        const inner = this.#inner.get(presented);
        dispatchers.set(dispatch, inner ? inner.subscribe(dispatch) : () => { });
    }
    #unregisterProgressDispatcher(presented, dispatch) {
        const dispatchers = this.#progressDispatchers.get(presented);
        if (!dispatchers)
            return;
        dispatchers.get(dispatch)?.();
        dispatchers.delete(dispatch);
        if (dispatchers.size === 0)
            this.#progressDispatchers.delete(presented);
    }
    // Replace the inner progress and move every registered subscriber onto it.
    #setInner(presented, inner) {
        this.#inner.set(presented, inner);
        const dispatchers = this.#progressDispatchers.get(presented);
        if (!dispatchers)
            return;
        for (const [dispatch, unsubscribeOld] of dispatchers) {
            unsubscribeOld();
            dispatchers.set(dispatch, inner.subscribe(dispatch));
            dispatch();
        }
    }
}
// Canonicalize any id to its *presented* url, preserving the path suffix
// (`/a/@0/b`) and any pinned heads so sub-document and view-scoped urls keep
// their identity through the overlay. Plain document ids carry no path.
function presentedUrlOf(id) {
    if (isValidAutomergeUrl(id)) {
        const { documentId, segments, heads } = parseAutomergeUrl(id);
        return stringifyAutomergeUrl({ documentId, segments, heads });
    }
    return stringifyAutomergeUrl({ documentId: interpretAsDocumentId(id) });
}
